import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MessageCircle, ImagePlus, X, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { CONTACT, SERVICE_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-forest-line bg-forest-deep/60 px-4 py-3.5 text-white placeholder-white/35 text-base focus:outline-none focus:ring-2 focus:ring-leaf focus:border-leaf transition-[border-color,box-shadow] duration-200";

export const QuoteForm = ({ eyebrowNum = "06", eyebrowLabel = "Kontakt & Angebot" }) => {
    const [form, setForm] = useState({ name: "", contact: "", service: "", location: "", details: "" });
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        const onPreselect = (e) => setForm((f) => ({ ...f, service: e.detail }));
        window.addEventListener("preselect-service", onPreselect);
        return () => window.removeEventListener("preselect-service", onPreselect);
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (file) fd.append("photo", file);
            await axios.post(`${API}/quote`, fd);
            toast.success("Anfrage gesendet.", {
                description: "Wir melden uns innerhalb von 24 Stunden bei Ihnen.",
            });
            setForm({ name: "", contact: "", service: "", location: "", details: "" });
            setFile(null);
        } catch {
            toast.error("Senden fehlgeschlagen.", {
                description: `Bitte versuchen Sie es erneut oder rufen Sie uns an: ${CONTACT.phoneDisplay}`,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="angebot" className="relative bg-forest-deep grain py-20 lg:py-32 overflow-hidden" data-testid="quote-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
                    <Reveal>
                        <Eyebrow num={eyebrowNum} label={eyebrowLabel} dark />
                        <h2 className="font-display font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.12]">
                            Ihr Grundstück soll wieder gepflegt aussehen?
                        </h2>
                        <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed max-w-lg">
                            Schreiben Sie uns kurz, worum es geht – optional mit Foto.
                            Sie erhalten ein kostenloses, unverbindliches Festpreis-Angebot.
                        </p>
                        <div className="mt-10 space-y-4">
                            <a
                                data-testid="contact-phone-link"
                                href={CONTACT.phoneHref}
                                className="flex items-center gap-4 text-white group"
                            >
                                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center group-hover:bg-leaf transition-colors duration-200">
                                    <Phone size={17} />
                                </span>
                                <span className="text-lg font-semibold">{CONTACT.phoneDisplay}</span>
                            </a>
                            <a
                                data-testid="contact-email-link"
                                href={`mailto:${CONTACT.email}`}
                                className="flex items-center gap-4 text-white group"
                            >
                                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center group-hover:bg-leaf transition-colors duration-200">
                                    <Mail size={17} />
                                </span>
                                <span className="text-lg font-semibold">{CONTACT.email}</span>
                            </a>
                            <a
                                data-testid="contact-whatsapp-link"
                                href={CONTACT.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-4 text-white group"
                            >
                                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center group-hover:bg-leaf transition-colors duration-200">
                                    <MessageCircle size={17} />
                                </span>
                                <span className="text-lg font-semibold">WhatsApp schreiben</span>
                            </a>
                        </div>
                        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                            Antwort innerhalb von 24 Stunden · Landkreis Celle
                        </p>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <form
                            data-testid="quote-request-form"
                            onSubmit={onSubmit}
                            className="bg-forest-card border border-forest-line rounded-3xl p-7 sm:p-9 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.8)]"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-1">
                                    <label htmlFor="q-name" className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Name *</label>
                                    <input id="q-name" data-testid="input-name" required value={form.name} onChange={set("name")} placeholder="Ihr Name" className={inputCls} />
                                </div>
                                <div className="sm:col-span-1">
                                    <label htmlFor="q-contact" className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Telefon / E-Mail *</label>
                                    <input id="q-contact" data-testid="input-contact" required value={form.contact} onChange={set("contact")} placeholder="Wie erreichen wir Sie?" className={inputCls} />
                                </div>
                                <div className="sm:col-span-1">
                                    <label htmlFor="q-service" className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Leistung *</label>
                                    <select id="q-service" data-testid="select-service" required value={form.service} onChange={set("service")} className={`${inputCls} appearance-none ${form.service ? "" : "text-white/35"}`}>
                                        <option value="" disabled>Bitte wählen</option>
                                        {SERVICE_OPTIONS.map((o) => (
                                            <option key={o} value={o} className="text-ink">{o}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-1">
                                    <label htmlFor="q-location" className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Ort / PLZ *</label>
                                    <input id="q-location" data-testid="input-location" required value={form.location} onChange={set("location")} placeholder="z. B. Celle, 29221" className={inputCls} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="q-details" className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Kurze Beschreibung</label>
                                    <textarea id="q-details" data-testid="input-details" rows={4} value={form.details} onChange={set("details")} placeholder="Worum geht es? Fläche ca., aktueller Zustand …" className={`${inputCls} resize-none`} />
                                </div>
                                <div className="sm:col-span-2">
                                    <span className="block text-xs font-mono uppercase tracking-[0.18em] text-white/55 mb-2">Foto (optional – für eine schnelle Einschätzung)</span>
                                    <input
                                        ref={fileRef}
                                        id="q-photo"
                                        data-testid="input-photo-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    />
                                    {file ? (
                                        <div className="flex items-center justify-between gap-3 rounded-xl border border-leaf/40 bg-leaf/10 px-4 py-3.5">
                                            <span className="text-sm text-leaf-soft truncate">{file.name}</span>
                                            <button type="button" data-testid="remove-photo-btn" onClick={() => { setFile(null); fileRef.current.value = ""; }} className="text-white/60 hover:text-white shrink-0">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid="photo-upload-trigger"
                                            onClick={() => fileRef.current?.click()}
                                            className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-white/25 px-4 py-5 text-white/55 text-sm transition-[border-color,color] duration-200 hover:border-leaf hover:text-leaf-soft"
                                        >
                                            <ImagePlus size={17} />
                                            Foto auswählen
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button
                                data-testid="submit-quote-request-btn"
                                type="submit"
                                disabled={sending}
                                className="mt-6 w-full inline-flex items-center justify-center gap-2.5 bg-leaf hover:bg-white text-white hover:text-forest-deep font-semibold px-7 py-4 rounded-full text-base transition-[background-color,color,transform] duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
                            >
                                {sending ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={17} strokeWidth={2.4} />}
                                {sending ? "Wird gesendet …" : "Kostenloses Angebot anfragen"}
                            </button>
                            <p className="mt-4 text-center text-xs text-white/40">
                                Unverbindlich &amp; kostenfrei. Ihre Daten werden nur zur Bearbeitung der Anfrage genutzt.
                            </p>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
